// eslint-disable-next-line no-restricted-imports
import { useTheme as useEmotionTheme } from '@emotion/react';
import type { CustomThemeConfig, ReeferTheme } from '@iheartjane/reefer';
import get from 'lodash-es/get';
import isArray from 'lodash-es/isArray';
import set from 'lodash-es/set';
import { parseToRgb } from 'polished';

import { contrastingColor } from './contrastingColor';

type ThemeData = Partial<CustomThemeConfig> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

const BLACK = '#000000';
const WHITE = '#FFFFFF';

const toHex = (color: string): string | null => {
  try {
    const { red, green, blue } = parseToRgb(color);
    return `#${[red, green, blue].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
  } catch {
    return null;
  }
};

/**
 * Determines the best contrasting text color (black or white) for a given
 * background color using WCAG contrast ratio guidelines.
 *
 * Accepts any CSS color format (hex, named, rgb, hsl). Falls back to white
 * if the input cannot be parsed.
 */
export const contrastTextColor = (backgroundColor: string): string => {
  const hex = toHex(backgroundColor);
  if (!hex) return WHITE;
  return contrastingColor(hex, [BLACK, WHITE]) ?? WHITE;
};

/**
 * ## Checkout Theme Mapping
 *
 * Maps flat theme keys (sent via postMessage to the headless cart iframe)
 * to Reefer's `CustomThemeConfig` paths.
 *
 * ### Legacy theme shape (v1)
 *
 * ```ts
 * {
 *   themeColor: string,           // → colors.primary
 *   navigationColor: string,      // → colors.secondary
 *   backgroundColor: string,      // → colors.background
 *   calloutBackground: string,    // → colors.info
 *   ctaTextColor: string,         // → colors.text.inverse
 *   calloutTextColor: string,     // → colors.text.info
 *   buttonBorderRadius: string,   // → components.Button.*.styles.borderRadius
 *   font: {
 *     fontFamily: string,         // → both default + branded font
 *     url: string,
 *   }
 * }
 * ```
 *
 * ### New theme shape (v2)
 *
 * ```ts
 * {
 *   themeColor: string,           // → colors.primary (unchanged)
 *   dealColor: string,            // → colors.info (replaces calloutBackground)
 *   font: {
 *     default: { fontFamily, url },
 *     header: { fontFamily, url },  // maps to Reefer's "branded" font
 *   }
 * }
 * ```
 *
 * **Key changes in v2:**
 * - `calloutBackground` → `dealColor` (same Reefer target: `colors.info`)
 * - `ctaTextColor` → auto-derived: black or white based on `themeColor` contrast
 * - `calloutTextColor` → auto-derived: black or white based on `dealColor` contrast
 * - Dropped: `navigationColor`, `backgroundColor`, `buttonBorderRadius`
 *
 * Auto-derivation only activates when `dealColor` is present. Legacy keys
 * still work for backwards compatibility; new keys take priority.
 */
const THEME_MAPPING = {
  // --- Shared (v1 + v2) ---
  themeColor: 'colors.primary',

  // --- v1 legacy keys (still supported for backwards compatibility) ---
  navigationColor: 'colors.secondary',
  calloutBackground: 'colors.info',
  backgroundColor: 'colors.background',
  ctaTextColor: 'colors.text.inverse',
  calloutTextColor: 'colors.text.info',
  buttonBorderRadius: [
    'components.Button.primary.styles.borderRadius',
    'components.Button.secondary.styles.borderRadius',
    'components.Button.tertiary.styles.borderRadius',
    'components.Button.tertiary-selected.styles.borderRadius',
    'components.Button.destructive.styles.borderRadius',
    'components.Button.minimal.styles.borderRadius',
  ],

  // --- v2 new keys (processed after legacy keys so they take priority) ---
  dealColor: 'colors.info',

  // --- Font mappings ---
  'font.color': 'colors.text.main',
  // Legacy font mappings - sets BOTH fonts to the same value (backwards compatible)
  'font.fontFamily': [
    'components.Typography.fonts.default.fontFamily',
    'components.Typography.fonts.branded.fontFamily',
  ],
  'font.url': [
    'components.Typography.fonts.default.url',
    'components.Typography.fonts.branded.url',
  ],
  // Premium/v2 font mappings - allows setting default and branded fonts independently
  'font.default.fontFamily': 'components.Typography.fonts.default.fontFamily',
  'font.default.url': 'components.Typography.fonts.default.url',
  'font.branded.fontFamily': 'components.Typography.fonts.branded.fontFamily',
  'font.branded.url': 'components.Typography.fonts.branded.url',
  // v2 alias: "header" maps to Reefer's "branded" font
  'font.header.fontFamily': 'components.Typography.fonts.branded.fontFamily',
  'font.header.url': 'components.Typography.fonts.branded.url',
};

export const LEGACY_COLORS = {
  black: '#1A3C34',
  fashionPink: '#FF9CCE',
  grassGreen: '#2BB967',
  midnightGreen: '#1A3C34',
  white: '#FFFFFF',
};

export const DEFAULT_THEME: CustomThemeConfig = {
  colors: {
    secondary: LEGACY_COLORS.midnightGreen,
    info: LEGACY_COLORS.fashionPink,
    text: {
      info: LEGACY_COLORS.white,
      inverse: LEGACY_COLORS.white,
    },
  },
  components: {
    Typography: {
      fonts: {
        branded: {
          fontFamily:
            'Jane Default, Source Sans Pro, Helvetica, Arial, sans-serif',
        },
      },
      variants: {},
    },
  },
};

export const DEFAULT_OPERATOR_THEME: CustomThemeConfig = {
  ...DEFAULT_THEME,
  colors: {
    ...DEFAULT_THEME.colors,
    primary: LEGACY_COLORS.black,
    secondary: LEGACY_COLORS.black,
  },
};

export const useTheme = (fallbackTheme = DEFAULT_THEME) =>
  (useEmotionTheme() || fallbackTheme) as ReeferTheme;

export const getTheme = (customTheme?: ThemeData) => {
  const mergedCustomTheme = { ...DEFAULT_THEME };

  Object.keys(THEME_MAPPING).forEach((customKey) => {
    const themeKey = THEME_MAPPING[customKey as keyof typeof THEME_MAPPING];
    const customValue = get(customTheme, customKey);

    if (customValue) {
      if (isArray(themeKey)) {
        themeKey.forEach((path) => set(mergedCustomTheme, path, customValue));
      } else {
        set(mergedCustomTheme, themeKey, customValue);
      }
    }
  });

  // When `dealColor` is present (v2 theme shape), auto-derive contrast text
  // colors unless explicitly overridden by the caller.
  const dealColor = get(customTheme, 'dealColor');
  const themeColor = get(customTheme, 'themeColor');

  if (dealColor) {
    if (!get(customTheme, 'ctaTextColor') && themeColor) {
      set(
        mergedCustomTheme,
        'colors.text.inverse',
        contrastTextColor(themeColor)
      );
    }

    if (!get(customTheme, 'calloutTextColor')) {
      set(mergedCustomTheme, 'colors.text.info', contrastTextColor(dealColor));
    }
  }

  return mergedCustomTheme;
};

export const getOperatorTheme = (customTheme?: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
  navigation_color?: string | null;
  theme_color?: string | null;
}) => {
  return {
    ...DEFAULT_OPERATOR_THEME,
    colors: {
      primary:
        customTheme?.theme_color || DEFAULT_OPERATOR_THEME?.colors?.primary,
      secondary:
        customTheme?.navigation_color ||
        DEFAULT_OPERATOR_THEME?.colors?.secondary,
    },
  };
};

export const getStoreTheme = (customTheme?: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
  custom_navigation_color?: string | null;
  custom_theme_color?: string | null;
}) => {
  return {
    ...DEFAULT_THEME,
    colors: {
      primary:
        customTheme?.custom_theme_color || DEFAULT_THEME?.colors?.primary,
      secondary:
        customTheme?.custom_navigation_color ||
        DEFAULT_THEME?.colors?.secondary,
    },
  };
};
